// Publish to RGM — review metadata, credits and confirm. Uses the same
// Submit Music pipeline + review status on the server.
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import CollabPicker from "../CollabPicker";
import { useCurrentUser } from "../../lib/useCurrentUser";

const newKey = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

export function PublishDialog({ open, onClose, projectId, mix, onPublished }) {
  const { currentUser } = useCurrentUser();
  const [artist, setArtist] = useState("");
  const [collabs, setCollabs] = useState([]);
  const [agree, setAgree] = useState(false);
  const [sending, setSending] = useState(false);
  const [pct, setPct] = useState(0);
  const [done, setDone] = useState(null);
  const keyRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    keyRef.current = newKey(); // one key per confirmation → retries can't double-post
    setAgree(false); setDone(null); setPct(0);
  }, [open]);
  useEffect(() => {
    if (currentUser && !artist) setArtist(currentUser.artist_name || currentUser.name || "");
  }, [currentUser]); // eslint-disable-line

  const p = mix?.project || {};
  const cover = p.cover_url || p.beat_ref?.cover_url;
  const missing = [!p.title && "song title", !p.genre && "genre", !p.description && "description", !cover && "cover", !artist.trim() && "artist name"].filter(Boolean);

  const publish = async () => {
    if (sending || !mix) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("music_file", new File([mix.blob], "final-mix.mp3", { type: "audio/mpeg" }));
      fd.append("artist_name", artist.trim());
      fd.append("publish_key", keyRef.current);
      fd.append("confirm", "yes");
      fd.append("duration_ms", String(mix.durationMs));
      fd.append("rms_db", String(mix.rmsDb));
      fd.append("peak_db", String(mix.peakDb));
      fd.append("fingerprint", mix.fingerprint || "");
      if (collabs.length) fd.append("featured_artists_json", JSON.stringify(collabs));
      const r = await api.post(`/studio/projects/${projectId}/publish`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => e.total && setPct(Math.round((e.loaded * 100) / e.total)),
      });
      setDone(r.data.submission_id);
      toast.success("Submitted to RGM!", { description: "Your song is in the normal review queue." });
      onPublished?.(r.data.submission_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publishing failed — nothing was posted. You can try again.");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !sending && onClose()}>
      <DialogContent className="bg-[#0f0b1a] border-amber-300/30 text-white max-h-[90vh] overflow-y-auto" data-testid="publish-dialog">
        <DialogHeader>
          <DialogTitle className="font-black">Publish to RGM</DialogTitle>
          <DialogDescription className="text-gray-400 text-xs">Goes through the same review as Submit Music. Your Studio project stays as it is.</DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="space-y-3 text-center py-4" data-testid="publish-success">
            <p className="font-black text-emerald-300">Submitted!</p>
            <Link to={`/track/${done}`} className="inline-block text-sm underline text-amber-300" data-testid="publish-view-track-link">View track</Link>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex gap-3">
              {cover ? <img src={cover} alt="" className="w-20 h-20 rounded-xl object-cover" data-testid="publish-cover" />
                : <div className="w-20 h-20 rounded-xl bg-white/5 flex items-center justify-center text-[10px] text-red-300">No cover</div>}
              <div className="min-w-0 text-sm">
                <p className="font-black truncate" data-testid="publish-title">{p.title || "—"}</p>
                <p className="text-xs text-gray-400" data-testid="publish-genre">{p.genre || "No genre"}</p>
                <p className="text-xs text-gray-500 line-clamp-2" data-testid="publish-description">{p.description || "No description"}</p>
                {(p.tags || []).length > 0 && <p className="text-[10px] text-fuchsia-300 mt-1" data-testid="publish-tags">{p.tags.map((t) => `#${t}`).join(" ")}</p>}
              </div>
            </div>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-gray-400 font-bold">Artist name</span>
              <input value={artist} maxLength={80} onChange={(e) => setArtist(e.target.value)} data-testid="publish-artist-input"
                className="mt-1 w-full h-11 px-3 rounded-xl bg-black/40 border border-white/10 text-white focus:outline-none focus:border-amber-300/60" />
            </label>
            <div>
              <span className="text-[10px] uppercase tracking-widest text-gray-400 font-bold">Featured artists & credits</span>
              <div className="mt-1"><CollabPicker value={collabs} onChange={setCollabs} currentUserId={currentUser?.user_id} /></div>
            </div>
            {missing.length > 0 && <p className="text-xs text-red-300" data-testid="publish-missing">Add {missing.join(", ")} in Project Details before publishing.</p>}
            <label className="flex gap-2 items-start text-xs text-gray-300">
              <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5 w-4 h-4 accent-amber-400" data-testid="publish-confirm-checkbox" />
              I listened to the final mix, I have the rights to this song and beat, and I want to submit it to RGM.
            </label>
            <button type="button" onClick={publish} disabled={!agree || sending || missing.length > 0}
              className="w-full h-12 rounded-xl bg-amber-400 hover:bg-amber-300 text-black font-black disabled:opacity-40" data-testid="publish-submit-btn">
              {sending ? `Uploading… ${pct}%` : "Confirm & publish"}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
